import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SearchPanel } from "@/components/SearchPanel";
import { ListingCard } from "@/components/ListingCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { slugifyPath, useHref, useLang, useT } from "@/i18n";

export default function Directory() {
  const t = useT();
  const href = useHref();
  const lang = useLang();
  const [params, setParams] = useSearchParams();
  const [locations, setLocations] = useState({ states: {}, categories: [] });
  const [data, setData] = useState({ items: [], total: 0 });
  const [nearby, setNearby] = useState({ items: [], district: "" });
  const [loading, setLoading] = useState(true);

  const filters = useMemo(
    () => ({
      q: params.get("q") || "",
      state: params.get("state") || "",
      district: params.get("district") || "",
      category: params.get("category") || "",
      page: Number(params.get("page") || 1),
    }),
    [params]
  );

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/listings", { params: { ...filters, limit: 24 } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!filters.state || !filters.district) {
      setNearby({ items: [], district: "" });
      return;
    }
    api
      .get("/listings", { params: { ...filters, page: 1, limit: 8, nearby: true } })
      .then(({ data: res }) =>
        setNearby({ items: res.items, district: res.nearby_district || filters.district })
      );
  }, [filters]);

  const apply = (next) => {
    const query = new URLSearchParams(Object.entries(next).filter(([, v]) => v));
    setParams(query);
  };

  // Remember where the visitor was so "Kembali ke direktori" lands on the same card.
  const scrollKey = `dirbiz_dir_scroll_${params.toString()}`;
  const rememberScroll = () => sessionStorage.setItem(scrollKey, String(window.scrollY));

  useEffect(() => {
    if (loading) return;
    const saved = Number(sessionStorage.getItem(scrollKey) || 0);
    if (saved <= 0) return;
    // The grid grows over several paints (cards + images), so keep nudging until it fits.
    let tries = 0;
    const timers = [];
    const restore = () => {
      window.scrollTo({ top: saved, behavior: "instant" });
      tries += 1;
      if (Math.abs(window.scrollY - saved) > 4 && tries < 40) {
        timers.push(setTimeout(restore, 60));
      }
    };
    restore();
    return () => timers.forEach(clearTimeout);
  }, [loading, scrollKey]);

  const pages = Math.max(1, Math.ceil(data.total / 24));

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t("directory.title")}</h1>
      <p className="mt-2 max-w-2xl text-base leading-relaxed text-slate-600">{t("directory.subtitle")}</p>

      <div className="mt-8">
        <SearchPanel locations={locations} value={filters} onSearch={apply} />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <p data-testid="results-count" className="text-sm font-medium text-slate-600">
          {t("directory.results", { count: data.total })}
        </p>
        {(filters.q || filters.state || filters.category) && (
          <Button variant="outline" size="sm" data-testid="clear-filters-button" onClick={() => setParams(new URLSearchParams())}>
            {t("directory.clear")}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"].map((k) => (
            <Skeleton key={k} className="h-80 w-full rounded-lg" />
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <div data-testid="empty-state" className="mt-12 rounded-lg border border-dashed border-border bg-card p-12 text-center">
          <p className="font-display text-lg font-semibold">{t("directory.empty.title")}</p>
          <p className="mt-2 text-sm text-slate-500">{t("directory.empty.body")}</p>
        </div>
      ) : (
        <div
          data-testid="directory-grid"
          onClickCapture={rememberScroll}
          className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
        >
          {data.items.map((l, i) => (
            <ListingCard key={l.id} listing={l} index={i} />
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            data-testid="prev-page-button"
            disabled={filters.page <= 1}
            onClick={() => apply({ ...filters, page: filters.page - 1 })}
          >
            {t("directory.prev")}
          </Button>
          <span className="text-sm text-slate-600">
            {t("directory.page", { page: filters.page, pages })}
          </span>
          <Button
            variant="outline"
            data-testid="next-page-button"
            disabled={filters.page >= pages}
            onClick={() => apply({ ...filters, page: filters.page + 1 })}
          >
            {t("directory.next")}
          </Button>
        </div>
      )}

      {nearby.items.length > 0 && (
        <section className="mt-16 border-t border-border pt-10" data-testid="nearby-section">
          <h2 className="font-display text-lg font-bold tracking-tight md:text-lg">
            {t("directory.nearby.title", { district: nearby.district })}
          </h2>
          <p className="mt-2 text-sm text-slate-600">{t("directory.nearby.body")}</p>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="nearby-grid">
            {nearby.items.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} />
            ))}
          </div>
        </section>
      )}

      <section className="mt-16 border-t border-border pt-10" data-testid="browse-by-location">        <h2 className="font-display text-lg font-bold tracking-tight md:text-lg">{t("seo.browse.title")}</h2>
        <p className="mt-2 text-sm text-slate-600">{t("seo.browse.body")}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {Object.keys(locations.states || {}).map((s) => (
            <Link
              key={s}
              to={`${href("/direktori")}/${lang === "ms" ? "semua" : "all"}/${slugifyPath(s)}`}
              data-testid={`browse-state-${slugifyPath(s)}`}
              className="rounded-full border border-border bg-white px-4 py-2 text-sm text-slate-700 transition-colors duration-200 hover:border-primary hover:text-primary"
            >
              {s}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
